import type { DraggableAttributes, DraggableSyntheticListeners } from "@dnd-kit/core";
import type {
  AdminPackagePayload,
  AdminQuestionPayload,
} from "@/features/admin/marketplace/types/admin-marketplace.types";

/** Drag-handle props produced by `useSortable`, spread onto the grip button. */
export interface DragHandleProps {
  attributes: DraggableAttributes;
  listeners: DraggableSyntheticListeners;
  isDragging: boolean;
}

/** Anchor ids for each form step — shared by the section cards and the nav rail. */
export const SECTION_ID = {
  basics: "vf-section-basics",
  agreement: "vf-section-agreement",
  packages: "vf-section-packages",
  questions: "vf-section-questions",
} as const;

export type SectionKey = keyof typeof SECTION_ID;

/**
 * Client mirrors of the server-side limits in
 * `services/api/src/schemas/marketplace.schemas.ts`. Kept in sync so the form
 * can validate inline and never submit a payload the API will reject.
 */
export const VENDOR_FORM_LIMITS = {
  questionPromptMax: 500,
  questionOptionMax: 128,
  questionOptionsMaxCount: 50,
  selectOptionMinCount: 2,
  questionCountMax: 50,
  packageCountMin: 1,
  packageCountMax: 5,
  currencyCodeLength: 3,
} as const;

/**
 * Max lengths mirror the server schema in
 * `services/api/src/schemas/marketplace.schemas.ts` so inputs cap at the same
 * boundary the API enforces and never submit an over-long payload.
 */
export const VENDOR_TEXT_LIMITS = {
  nameMax: 255,
  descriptionMax: 2_000,
  websiteUrlMax: 500,
  contactEmailMax: 255,
} as const;

/** Mirrors `SlugRegex` in the marketplace schema. */
const VENDOR_SLUG_REGEX = /^[a-z0-9][a-z0-9_-]*$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Text fields in the Basics + Agreement sections that have inline validation. */
export type VendorBasicsField =
  | "name"
  | "slug"
  | "websiteUrl"
  | "termsMarkdown";

/** Maps an invalid field to the i18n key describing the problem. */
export type VendorFieldErrors = Partial<Record<VendorBasicsField, string>>;

const ERROR_KEY_PREFIX = "marketplace.admin.form.errors.";

function isHttpUrl(value: string): boolean {
  try {
    const { protocol } = new URL(value);
    return protocol === "http:" || protocol === "https:";
  } catch {
    return false;
  }
}

/**
 * Validates the free-text vendor fields, returning i18n error keys for each
 * invalid field. Optional fields (website, email) only error when filled.
 */
export function validateVendorBasics(fields: {
  name: string;
  slug: string;
  websiteUrl: string;
  termsMarkdown: string;
}): VendorFieldErrors {
  const errors: VendorFieldErrors = {};

  if (!fields.name.trim()) {
    errors.name = `${ERROR_KEY_PREFIX}nameRequired`;
  }

  const slug = fields.slug.trim();
  if (!slug) {
    errors.slug = `${ERROR_KEY_PREFIX}slugRequired`;
  } else if (!VENDOR_SLUG_REGEX.test(slug)) {
    errors.slug = `${ERROR_KEY_PREFIX}slugInvalid`;
  }

  const website = fields.websiteUrl.trim();
  if (website && !isHttpUrl(website)) {
    errors.websiteUrl = `${ERROR_KEY_PREFIX}websiteUrlInvalid`;
  }

  if (!fields.termsMarkdown.trim()) {
    errors.termsMarkdown = `${ERROR_KEY_PREFIX}termsRequired`;
  }

  return errors;
}

/**
 * Validates the vendor's address list as a whole. Returns per-row error keys
 * alongside a list-level key, because "you have two of the same address" is not
 * a property of either row on its own.
 */
export interface VendorContactEmailErrors {
  rows: Record<string, string>;
  list?: string;
}

export function validateVendorContactEmails(
  emails: Array<{ uid: string; email: string }>,
): VendorContactEmailErrors {
  const rows: Record<string, string> = {};
  const seen = new Set<string>();
  let hasDuplicate = false;
  let filledCount = 0;

  for (const row of emails) {
    const value = row.email.trim();
    if (!value) {
      rows[row.uid] = `${ERROR_KEY_PREFIX}contactEmailRequired`;
      continue;
    }
    filledCount++;
    if (!EMAIL_REGEX.test(value)) {
      rows[row.uid] = `${ERROR_KEY_PREFIX}contactEmailInvalid`;
      continue;
    }
    const key = value.toLowerCase();
    if (seen.has(key)) {
      rows[row.uid] = `${ERROR_KEY_PREFIX}contactEmailDuplicate`;
      hasDuplicate = true;
      continue;
    }
    seen.add(key);
  }

  const list = filledCount === 0 ? `${ERROR_KEY_PREFIX}contactEmailsRequired` : undefined;
  return hasDuplicate || Object.keys(rows).length > 0 || list ? { rows, list } : { rows: {} };
}

/**
 * Picks the address to mirror into the vendor's legacy single `contactEmail`.
 *
 * The remaining readers of that column send without consulting approval, so only
 * an APPROVED address may be mirrored — otherwise marking a brand-new (pending)
 * address as primary would quietly turn it into an egress target. Prefers the
 * chosen primary when it is approved, falling back to the first other approved
 * address so revoking the primary does not strand a vendor with no legacy value.
 */
export function resolveLegacyContactEmail(
  addresses: Array<{ uid: string; email: string; approval: "APPROVED" | "PENDING" }>,
  primaryUid: string | undefined,
): string | null {
  const approved = addresses.filter((row) => row.approval === "APPROVED");
  const chosen = approved.find((row) => row.uid === primaryUid) ?? approved[0];
  return chosen?.email ?? null;
}

export type QuestionType = AdminQuestionPayload["type"];

/**
 * UI metadata for each guideline question type. The three types are fixed by
 * the `VendorQuestionType` enum — TEXT/TEXTAREA ignore options, SELECT requires
 * a minimum of two. `icon` is a Lucide icon name resolved by the editor.
 */
export const QUESTION_TYPE_META: Record<
  QuestionType,
  { labelKey: string; descKey: string; icon: "Type" | "AlignLeft" | "ListChecks"; hasOptions: boolean }
> = {
  TEXT: {
    labelKey: "marketplace.admin.form.typeText",
    descKey: "marketplace.admin.form.typeTextDesc",
    icon: "Type",
    hasOptions: false,
  },
  TEXTAREA: {
    labelKey: "marketplace.admin.form.typeTextarea",
    descKey: "marketplace.admin.form.typeTextareaDesc",
    icon: "AlignLeft",
    hasOptions: false,
  },
  SELECT: {
    labelKey: "marketplace.admin.form.typeSelect",
    descKey: "marketplace.admin.form.typeSelectDesc",
    icon: "ListChecks",
    hasOptions: true,
  },
};

/**
 * Editable rows carry a stable client-only `uid` so React keys survive reorder
 * and delete (index keys drop focus and mis-map inputs). Stripped before submit.
 */
export interface EditablePackage extends AdminPackagePayload {
  uid: string;
}

export interface EditableQuestion extends AdminQuestionPayload {
  uid: string;
  /** Client-only stable keys, parallel to `options`, for drag reorder. Stripped on submit. */
  optionUids: string[];
}

let uidCounter = 0;
function nextUid(prefix: string): string {
  uidCounter += 1;
  return `${prefix}-${Date.now().toString(36)}-${uidCounter}`;
}

export function createOptionUid(): string {
  return nextUid("opt");
}

export function createEmptyPackage(): EditablePackage {
  return {
    uid: nextUid("pkg"),
    name: "",
    description: null,
    price: null,
    currency: null,
    cadence: null,
    isPopular: false,
    isActive: true,
    sortOrder: 0,
  };
}

export function createEmptyQuestion(): EditableQuestion {
  return {
    uid: nextUid("q"),
    prompt: "",
    type: "TEXT",
    options: [],
    optionUids: [],
    allowMultiple: true,
    isRequired: true,
    sortOrder: 0,
  };
}

export function toEditablePackage(pkg: AdminPackagePayload): EditablePackage {
  return { ...pkg, uid: nextUid("pkg") };
}

export function toEditableQuestion(q: AdminQuestionPayload): EditableQuestion {
  const options = q.options ?? [];
  return {
    ...q,
    options,
    optionUids: options.map(() => nextUid("opt")),
    uid: nextUid("q"),
  };
}

/** A blocking sub-entity (package/question) problem found before submit. */
export interface VendorSubEntityError {
  /** Which collapsible section to re-open so the problem is visible. */
  section: "packages" | "questions";
  /** i18n key under `marketplace.admin.form.errors.`. */
  messageKey: string;
  /** Interpolation values for the message (e.g. `count`). */
  values?: Record<string, number>;
}

/**
 * Validates packages and SELECT questions the way the API will, so the form
 * never submits a payload the server would reject with an opaque 400. Returns
 * the first problem found (the form re-opens its section and surfaces it), or
 * `null` when the sub-entities are valid. Mirrors the superRefine rules in
 * `services/api/src/schemas/marketplace.schemas.ts`.
 */
export function validateVendorSubEntities(
  packages: EditablePackage[],
  questions: EditableQuestion[],
): VendorSubEntityError | null {
  if (packages.length > VENDOR_FORM_LIMITS.packageCountMax) {
    return {
      section: "packages",
      messageKey: "packagesMax",
      values: { count: VENDOR_FORM_LIMITS.packageCountMax },
    };
  }

  if (packages.some((p) => !p.name.trim())) {
    return { section: "packages", messageKey: "packageNameRequired" };
  }

  for (const q of questions) {
    if (!q.prompt.trim()) {
      return { section: "questions", messageKey: "questionPromptRequired" };
    }
    if (q.type !== "SELECT") continue;

    const filled = (q.options ?? []).map((o) => o.trim()).filter(Boolean);
    if (filled.length < VENDOR_FORM_LIMITS.selectOptionMinCount) {
      return {
        section: "questions",
        messageKey: "selectNeedsOptions",
        values: { count: VENDOR_FORM_LIMITS.selectOptionMinCount },
      };
    }
    if (new Set(filled).size !== filled.length) {
      return { section: "questions", messageKey: "selectDuplicateOptions" };
    }
  }

  return null;
}

/**
 * Whether an approve/revoke call may act on this contact email row.
 *
 * Approval targets the stored row by id, so an unsaved row has nothing to
 * approve, and a row whose field has been edited since loading would approve an
 * address other than the one on screen.
 */
export function isContactEmailPersisted(row: {
  id?: string;
  email: string;
  persistedEmail?: string;
}): boolean {
  if (!row.id) return false;
  return row.email.trim() === row.persistedEmail;
}

/** Move an item within an array by direction, returning a new array. */
export function moveItem<T>(items: T[], index: number, direction: "up" | "down"): T[] {
  const target = direction === "up" ? index - 1 : index + 1;
  if (target < 0 || target >= items.length) return items;
  const next = [...items];
  [next[index], next[target]] = [next[target], next[index]];
  return next;
}
